import { useCallback } from 'react';
import { gamesDB, preGameIntentsDB, miniBlocksDB, userSettingsDB, rankSnapshotsDB } from '../lib/supabase';
import { getLeagueEntries } from '../lib/riot';
import { updateMissionProgress } from '../lib/dailyMission';
import { log } from '../lib/logger';

/**
 * APEX v8.18 — useGames
 *
 * Encapsule tous les handlers CRUD des games et la logique associée :
 *   - addGame       : ajout manuel + auto-match intent
 *   - importRiotGame: import depuis l'API Riot + mission du jour + rank snapshot
 *   - updateGame    : mise à jour avec historique d'édition
 *   - deleteGame    : suppression avec confirm
 *
 * Dépend des setters exposés par DataContext (via useData).
 */
export function useGames({
  games,
  setGames,
  pendingIntent,
  setPendingIntent,
  setAllIntents,
  miniBlocks,
  setMiniBlocks,
  dailyMission,
  userSettings,
  setUserSettings,
  rankSnapshots,
  setRankSnapshots,
  editingGame,
  setEditingGame,
  setShowForm,
}) {
  // ============ ADD GAME (saisie manuelle) ============
  const addGame = useCallback(async (game) => {
    const saved = await gamesDB.add(game);
    setGames(prev => [...prev, saved]);

    // Auto-match pending intent
    if (pendingIntent) {
      try {
        await preGameIntentsDB.matchToGame(pendingIntent.id, saved.id);
        setAllIntents(prev => prev.map(i =>
          i.id === pendingIntent.id ? { ...i, matched_game_id: saved.id } : i
        ));
        setPendingIntent(null);
      } catch (e) {
        console.error('Failed to match intent:', e);
      }
    }

    setShowForm(false);
  }, [games, pendingIntent, setGames, setPendingIntent, setAllIntents, setShowForm]);

  // ============ IMPORT RIOT GAME ============
  const importRiotGame = useCallback(async (riotGame) => {
    // v7.7.10 : dedupe vérifié EN BASE (closure figée dans auto-import → doublons potentiels)
    const saved = await gamesDB.addIfNotExists(riotGame);
    if (saved && saved.__duplicate) return;

    setGames(prev => {
      if (riotGame.riot_match_id && prev.some(g => g.riot_match_id === riotGame.riot_match_id)) {
        return prev;
      }
      return [...prev, saved];
    });

    // V7.7.0 : Évaluation de la mission du jour
    if (dailyMission && dailyMission.status === 'active') {
      try {
        const updated = updateMissionProgress(dailyMission, [saved]);
        const progressChanged =
          updated.status !== dailyMission.status ||
          updated.data.progress.gamesContributed !== dailyMission.data.progress?.gamesContributed;

        if (progressChanged) {
          await miniBlocksDB.update(dailyMission.id, {
            status: updated.status,
            total_games: updated.data.progress.gamesContributed,
            data: updated.data
          });
          setMiniBlocks(prev => prev.map(b =>
            b.id === dailyMission.id ? { ...b, ...updated } : b
          ));

          // Toast natif si mission complétée
          if (updated.status === 'completed' && dailyMission.status === 'active') {
            log.debug('[dailyMission] Mission accomplie ✓');
            if (typeof window !== 'undefined') {
              const el = document.createElement('div');
              el.textContent = '✓ Mission du jour accomplie !';
              el.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#22d3ee;color:#000;font-weight:900;font-size:12px;letter-spacing:0.15em;padding:10px 20px;z-index:9999;pointer-events:none;';
              document.body.appendChild(el);
              setTimeout(() => el.remove(), 3000);
            }
          }
        }
      } catch (e) {
        console.error('[dailyMission] update failed:', e);
      }
    }

    // Auto-snapshot rang en background (non-bloquant)
    if (userSettings?.riot_puuid) {
      getLeagueEntries(userSettings.riot_puuid, userSettings.riot_region || 'euw1')
        .then(async entries => {
          const soloQ = entries.find(e => e.queueType === 'RANKED_SOLO_5x5');
          if (!soloQ) return;

          const updated = await userSettingsDB.upsert({
            riot_game_name: userSettings.riot_game_name,
            riot_tag_line: userSettings.riot_tag_line,
            riot_puuid: userSettings.riot_puuid,
            riot_region: userSettings.riot_region,
            riot_platform: userSettings.riot_platform,
            current_tier: soloQ.tier,
            current_rank: soloQ.rank,
            current_lp: soloQ.leaguePoints,
            current_wins: soloQ.wins,
            current_losses: soloQ.losses,
            rank_updated_at: new Date().toISOString()
          });
          setUserSettings(updated);

          // V6.7 : Snapshot systématique, dedup 30s
          const lastSnap = rankSnapshots[rankSnapshots.length - 1];
          const tooRecent = lastSnap && (new Date() - new Date(lastSnap.captured_at) < 30000);
          if (!tooRecent) {
            const snapshot = await rankSnapshotsDB.add({
              tier: soloQ.tier,
              rank: soloQ.rank,
              lp: soloQ.leaguePoints,
              wins: soloQ.wins,
              losses: soloQ.losses,
              game_id: saved.id
            });
            setRankSnapshots(prev => [...prev, snapshot]);
          }
        })
        .catch(e => console.warn('Auto rank refresh failed:', e));
    }

    return saved;
  }, [dailyMission, miniBlocks, userSettings, rankSnapshots,
    setGames, setMiniBlocks, setUserSettings, setRankSnapshots]);

  // ============ UPDATE GAME ============
  const updateGame = useCallback(async (game) => {
    const original = games.find(g => g.id === editingGame.id);
    const previousSnapshot = { ...original };
    delete previousSnapshot.id;
    delete previousSnapshot.edit_history;
    delete previousSnapshot.created_at;
    delete previousSnapshot.updated_at;

    const newHistory = await gamesDB.update(editingGame.id, game, previousSnapshot);
    setGames(prev =>
      prev.map(g => g.id === editingGame.id
        ? { ...g, ...game, edit_history: newHistory }
        : g
      )
    );
    setEditingGame(null);
  }, [games, editingGame, setGames, setEditingGame]);

  // ============ DELETE GAME ============
  const deleteGame = useCallback(async (id) => {
    if (!confirm('Supprimer cette game ?')) return;
    try {
      await gamesDB.delete(id);
      setGames(prev => prev.filter(g => g.id !== id));
    } catch (e) {
      alert('Erreur : ' + e.message);
    }
  }, [setGames]);

  return { addGame, importRiotGame, updateGame, deleteGame };
}
